import { useState, useEffect } from "react";
import { format, addDays, isAfter, isBefore, startOfDay, addMinutes, setHours, setMinutes } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { CalendarIcon, Clock, User, Mail, Phone, Building2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

import { z } from "zod";

const appointmentSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }).max(100),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  company: z.string().trim().max(100).optional().or(z.literal("")),
  website: z.string().trim().url({ message: "Website must be a valid URL" }).max(2048).optional().or(z.literal("")),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
  honeypot: z.string().max(0).optional().or(z.literal("")),
});

interface BookingSystemProps {
  language: 'es' | 'en';
}

interface ServiceOption {
  name: string;
  duration: number; // in minutes
  description: string;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

export const BookingSystem = ({ language }: BookingSystemProps) => {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [selectedService, setSelectedService] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    message: '',
    honeypot: '' // Hidden field to catch bots
  });

  const content = {
    es: {
      title: "Agendar Consultoría",
      subtitle: "Selecciona el servicio, fecha y hora que mejor te convenga",
      services: {
        title: "Tipo de Consultoría",
        options: [
          { name: "Reunión inicial gratuita", duration: 30, description: "30 minutos para revisar tu contexto y definir posibles siguientes pasos" }
        ]
      },
      date: {
        title: "Fecha",
        placeholder: "Selecciona una fecha"
      },
      time: {
        title: "Hora",
        placeholder: "Selecciona una hora"
      },
      form: {
        name: "Nombre completo",
        email: "Correo electrónico",
        phone: "Teléfono",
        company: "Empresa",
        website: "Sitio web",
        message: "¿En qué podemos ayudarte?",
        submit: "Confirmar Cita",
        submitting: "Agendando..."
      },
      messages: {
        selectService: "Selecciona un tipo de consultoría",
        selectDate: "Selecciona una fecha",
        selectTime: "Selecciona una hora",
        fillRequired: "Completa todos los campos requeridos",
        success: "¡Cita agendada exitosamente!",
        error: "Error al agendar la cita. Inténtalo de nuevo."
      },
      availability: "Los horarios disponibles se muestran según la disponibilidad real del calendario."
    },
    en: {
      title: "Schedule Consultation",
      subtitle: "Select the service, date and time that works best for you",
      services: {
        title: "Consultation Type",
        options: [
          { name: "Free initial consultation", duration: 30, description: "30 minutes to review your context and define possible next steps" }
        ]
      },
      date: {
        title: "Date",
        placeholder: "Select a date"
      },
      time: {
        title: "Time",
        placeholder: "Select a time"
      },
      form: {
        name: "Full name",
        email: "Email address",
        phone: "Phone",
        company: "Company",
        website: "Website",
        message: "How can we help you?",
        submit: "Confirm Appointment",
        submitting: "Booking..."
      },
      messages: {
        selectService: "Select a consultation type",
        selectDate: "Select a date",
        selectTime: "Select a time",
        fillRequired: "Fill in all required fields",
        success: "Appointment booked successfully!",
        error: "Error booking appointment. Please try again."
      },
      availability: "Available time slots are shown based on real calendar availability."
    }
  };

  const currentContent = content[language];
  const locale = language === 'es' ? es : enUS;

  // Generate available time slots using secure server-side endpoint
  const generateTimeSlots = async (date: Date, serviceDuration: number): Promise<TimeSlot[]> => {
    const dayOfWeek = date.getDay();
    
    // Only available on Tuesdays (2) and Thursdays (4)
    if (dayOfWeek !== 2 && dayOfWeek !== 4) {
      return [];
    }

    try {
      // Use secure server-side endpoint to check availability
      const { data, error } = await supabase.functions.invoke('check-availability', {
        body: {
          date: format(date, 'yyyy-MM-dd'),
          serviceDuration
        }
      });

      if (error) {
        console.error('Error checking availability:', error);
        return [];
      }

      return data?.availableSlots || [];
    } catch (error) {
      console.error('Error checking availability:', error);
      return [];
    }
  };

  // Update available slots when date or service changes
  useEffect(() => {
    const updateSlots = async () => {
      if (selectedDate && selectedService) {
        setIsLoading(true);
        const service = currentContent.services.options.find(s => s.name === selectedService);
        if (service) {
          const slots = await generateTimeSlots(selectedDate, service.duration);
          setAvailableSlots(slots);
        }
        setIsLoading(false);
      } else {
        setAvailableSlots([]);
      }
      setSelectedTime("");
    };

    updateSlots();
  }, [selectedDate, selectedService]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if date is available (Tuesdays and Thursdays only, not in the past)
  const isDateAvailable = (date: Date) => {
    const dayOfWeek = date.getDay();
    const today = startOfDay(new Date());
    return (dayOfWeek === 2 || dayOfWeek === 4) && isAfter(date, today);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot validation - silently drop bots
    if (formData.honeypot) {
      return;
    }

    // Schema validation
    const parsed = appointmentSchema.safeParse(formData);
    if (!parsed.success) {
      const msg = parsed.error.issues[0]?.message || (language === 'es' ? 'Datos inválidos' : 'Invalid data');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      return;
    }

    if (!selectedService || !selectedDate || !selectedTime) {
      toast({ title: 'Error', description: currentContent.messages.fillRequired, variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);

    try {
      const service = currentContent.services.options.find(s => s.name === selectedService);
      const d = parsed.data;
      const appointmentData = {
        name: d.name,
        email: d.email,
        phone: d.phone,
        company: d.company,
        website: d.website,
        message: d.message,
        serviceType: selectedService,
        serviceDuration: service?.duration || 30,
        appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
        appointmentTime: selectedTime,
        timezone: 'America/Chicago',
        language,
        honeypot: d.honeypot // Include for server-side validation
      };

      const { data, error } = await supabase.functions.invoke('book-appointment', {
        body: { appointmentData }
      });

      if (error) throw error;

      toast({
        title: "¡Éxito!",
        description: currentContent.messages.success,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        message: '',
        honeypot: ''
      });
      setSelectedDate(undefined);
      setSelectedTime("");
      setSelectedService("");

    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Error",
        description: currentContent.messages.error,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="w-full">
        <CardHeader className="text-center">
          <h1 className="text-2xl font-bold">{currentContent.title}</h1>
          <CardDescription className="text-lg">{currentContent.subtitle}</CardDescription>
          <p className="text-sm text-muted-foreground mt-2">{currentContent.availability}</p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Service Selection */}
            <div className="space-y-2">
              <Label className="text-base font-medium">{currentContent.services.title}</Label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger aria-label={currentContent.services.title}>
                  <SelectValue placeholder={currentContent.services.title} />
                </SelectTrigger>
                <SelectContent>
                  {currentContent.services.options.map((service) => (
                    <SelectItem key={service.name} value={service.name}>
                      <div className="flex flex-col">
                        <span>{service.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {service.duration} min - {service.description}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="text-base font-medium">{currentContent.date.title}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PPP", { locale }) : currentContent.date.placeholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => !isDateAvailable(date)}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                    locale={locale}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selection */}
            {availableSlots.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base font-medium">{currentContent.time.title}</Label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot.time}
                      type="button"
                      variant={selectedTime === slot.time ? "default" : "outline"}
                      className="h-10"
                      onClick={() => setSelectedTime(slot.time)}
                      disabled={!slot.available}
                    >
                      <Clock className="mr-1 h-3 w-3" />
                      {slot.time}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Form */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {currentContent.form.name} *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {currentContent.form.email} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {currentContent.form.phone}
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {currentContent.form.company}
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">
                {currentContent.form.website}
              </Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
              />
            </div>

            {/* Honeypot field - hidden from users but may be filled by bots */}
            <input
              type="text"
              name="website_url"
              value={formData.honeypot}
              onChange={(e) => handleInputChange('honeypot', e.target.value)}
              style={{ 
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                zIndex: -1
              }}
              tabIndex={-1}
              aria-hidden="true"
              autoComplete="off"
            />

            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {currentContent.form.message}
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={4}
              />
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting || !selectedService || !selectedDate || !selectedTime}
            >
              {isSubmitting ? currentContent.form.submitting : currentContent.form.submit}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};